import { useEffect } from 'react'
import { flushSync } from 'react-dom'
import { Monitor, Moon, Sun } from 'lucide-react'
import { prefersReducedMotion } from '@/hooks/useMediaQuery'
import { useUiStore, type ThemePreference } from '@/stores/uiStore'
import { IconButton } from '@/components/ui/IconButton'
import { Menu, MenuContent, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuTrigger } from '@/components/ui/Menu'

const icons = { light: Sun, dark: Moon, system: Monitor }
const labels: Record<ThemePreference, string> = { light: 'Light', dark: 'Dark', system: 'System' }

/** Last pointer position: the theme change ripples out from where the user clicked. */
const pointer = { x: 0, y: 0 }

function isDark(theme: ThemePreference): boolean {
  return theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
}

/** Switch theme with a circular reveal (View Transitions API), falling back to an instant switch. */
function switchTheme(theme: ThemePreference, set: (theme: ThemePreference) => void) {
  const root = document.documentElement
  const apply = () => {
    root.classList.toggle('dark', isDark(theme))
    set(theme)
  }
  if (!document.startViewTransition || prefersReducedMotion() || isDark(theme) === root.classList.contains('dark')) {
    apply()
    return
  }
  const { x, y } = pointer.x || pointer.y ? pointer : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
  const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))
  const transition = document.startViewTransition(() => flushSync(apply))
  void transition.ready.then(() => {
    root.animate(
      { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
      { duration: 650, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', pseudoElement: '::view-transition-new(root)' },
    )
  })
}

export function ThemeToggle() {
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  const Icon = icons[theme]

  useEffect(() => {
    const track = (event: PointerEvent) => {
      pointer.x = event.clientX
      pointer.y = event.clientY
    }
    window.addEventListener('pointerdown', track, { passive: true })
    return () => window.removeEventListener('pointerdown', track)
  }, [])

  return (
    <Menu>
      <MenuTrigger asChild>
        <IconButton label={`Theme: ${labels[theme]}`}>
          <Icon />
        </IconButton>
      </MenuTrigger>
      <MenuContent align="end" side="top" className="min-w-[9rem]">
        <MenuLabel>Theme</MenuLabel>
        <MenuRadioGroup value={theme} onValueChange={(v) => switchTheme(v as ThemePreference, setTheme)}>
          {(Object.keys(labels) as ThemePreference[]).map((value) => (
            <MenuRadioItem key={value} value={value}>
              {labels[value]}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  )
}
